import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import { Icon, useResponsive } from '@sg/garnish';
import { BodyText, IllusEmpty_2, theme, TitleText } from '@sg/garnish';

import { SweetpassHomeActionCardImage } from '../SweetpassHomeActionCardImage';
import type { SweetpassHomeActionCardLayoutVariantProps } from '../SweetpassHomeActionCardLayoutVariantProps';

// TODO: This constant should reflect the maximum dimension of
// the image at which it appears in the layout, rounded up to 1:1 aspect ratio.
// Eg. if the image appears at most w:400,h:300, this should be w:400,h:400
const LOADED_IMAGE_SIZE = {
  width: 600,
  height: 600,
};

export const SweetpassHomeActionCardLayoutRectangleFrame = (
  props: SweetpassHomeActionCardLayoutVariantProps,
) => {
  const {
    testID,
    style,
    theme: themeConfig,
    content: { image, headingText, bodyText },
    hasAction,
    ...otherProps
  } = props;

  const { match, currentBreakpoint } = useResponsive();
  const { isXS, isMD, isLG } = currentBreakpoint;
  const isHorizontalLayout = isXS || isMD || isLG;

  const { foregroundColor, backgroundColor } = themeConfig;

  const imageResponsiveStyle = match([
    styles.imgXS,
    styles.imgSM,
    styles.imgMD,
  ]);
  const imgStyle = [styles.img, imageResponsiveStyle];
  const textStyle = useStyle(
    () => ({ color: foregroundColor }),
    [foregroundColor],
  );
  const backgroundViewStyle = useStyle(
    () => ({ borderColor: foregroundColor, backgroundColor }),
    [foregroundColor, backgroundColor],
  );

  return (
    <View
      testID={testID}
      style={[
        style,
        backgroundViewStyle,
        styles.container,
        isLG && styles.containerLG,
        isHorizontalLayout && styles.containerHorizontal,
      ]}
      {...otherProps}
    >
      <SweetpassHomeActionCardImage
        testID={testID?.concat('.image')}
        width={LOADED_IMAGE_SIZE.width}
        height={LOADED_IMAGE_SIZE.height}
        defaultImage={IllusEmpty_2}
        style={imgStyle}
        image={image}
        theme={themeConfig}
      />

      <View style={isHorizontalLayout ? styles.actionCardInfoHorizontal : null}>
        <TitleText
          testID={testID?.concat('.heading')}
          size={isLG ? 3 : 5}
          style={[textStyle, !isHorizontalLayout && styles.headingText]}
        >
          {headingText}
        </TitleText>

        <View style={styles.bodyRow}>
          <BodyText testID={testID?.concat('.body')} size={3} style={textStyle}>
            {bodyText}
          </BodyText>

          {hasAction ? (
            <Icon
              name="IconArrowRight"
              testID={testID?.concat('.action-indicator')}
              color={foregroundColor}
            />
          ) : null}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: theme.spacing['4'],
    borderRadius: theme.spacing['4'],
    borderWidth: 1,
  },
  containerLG: {
    padding: theme.spacing['6'],
  },
  containerHorizontal: {
    minHeight: 240,
    flexDirection: 'row',
  },
  actionCardInfoHorizontal: {
    flex: 1,
    justifyContent: 'space-between',
    marginLeft: theme.spacing['4'],
  },
  headingText: {
    marginTop: theme.spacing['6'],
  },
  bodyRow: {
    marginTop: theme.spacing['6'],
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  img: {
    flex: 1,
    width: '100%',
    borderRadius: theme.spacing['2'],
    borderWidth: 1,
  },
  imgXS: {
    flex: 1,
    height: '100%',
  },
  imgSM: {
    minHeight: 168,
    maxHeight: 500,
  },
  imgMD: {
    flex: 1,
    height: '100%',
  },
});
